import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { cn } from "@/lib/utils";

interface PathEditorProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

const FRIENDLY_LABELS: Record<string, string> = {
  ':orderId': 'order #',
  ':id': '#',
  ':slug': 'page',
  ':productId': 'product #',
  ':customerId': 'customer #',
  ':token': 'token',
  ':sessionId': 'session',
  ':invoiceId': 'invoice #',
  ':subscriptionId': 'subscription #',
};

function friendlyLabel(variable: string): string {
  if (FRIENDLY_LABELS[variable]) return FRIENDLY_LABELS[variable];
  const name = variable.replace(/^:/, '').replace(/Id$/, ' #').replace(/([A-Z])/g, ' $1').trim().toLowerCase();
  return name;
}

/**
 * PathEditor — lets merchants edit the visible URL path segments.
 *
 * Static segments are editable on click.
 * Variable segments (`:orderId`, etc.) are shown as friendly read-only pills.
 */
export function PathEditor({ value, onChange, disabled = false, className }: PathEditorProps) {
  const { t } = useTranslation('storefront');
  const [segments, setSegments] = useState<Array<{ type: 'static' | 'variable'; value: string }>>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');

  useEffect(() => {
    const parts = value.split('/').filter(Boolean);
    setSegments(parts.map((part) => ({
      type: part.startsWith(':') ? 'variable' : 'static',
      value: part,
    })));
  }, [value]);

  const handleSegmentEdit = (index: number, newValue: string) => {
    const updated = [...segments];
    updated[index] = { ...updated[index], value: newValue };
    onChange('/' + updated.map(s => s.value).join('/'));
    setEditingIndex(null);
  };

  const startEditing = (index: number) => {
    if (disabled || segments[index].type === 'variable') return;
    setEditingIndex(index);
    setEditValue(segments[index].value);
  };

  const cancelEditing = () => {
    setEditingIndex(null);
    setEditValue('');
  };

  return (
    <div
      className={cn(
        'flex min-h-[40px] w-full flex-wrap items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
    >
      <span className="font-mono text-sm text-slate-400 dark:text-slate-500">/</span>
      {segments.map((segment, index) => (
        <React.Fragment key={index}>
          {segment.type === 'variable' ? (
            <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-xs text-slate-400 dark:bg-slate-800 dark:text-slate-500">
              {friendlyLabel(segment.value)}
            </span>
          ) : editingIndex === index ? (
            <Input
              autoFocus
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={() => {
                if (editValue.trim()) {
                  handleSegmentEdit(index, editValue.trim());
                } else {
                  cancelEditing();
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && editValue.trim()) {
                  handleSegmentEdit(index, editValue.trim());
                } else if (e.key === 'Escape') {
                  cancelEditing();
                }
              }}
              className="h-7 w-24 px-2 font-mono text-sm"
            />
          ) : (
            <button
              type="button"
              onClick={() => startEditing(index)}
              disabled={disabled}
              className={cn(
                'rounded-md px-2 py-0.5 font-mono text-sm font-medium transition-all',
                !disabled && 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700',
                disabled && 'cursor-not-allowed text-slate-500 dark:text-slate-400',
              )}
              title={!disabled ? t('pathEditor.clickToRename') : undefined}
            >
              {segment.value}
            </button>
          )}
          {index < segments.length - 1 && (
            <span className="font-mono text-sm text-slate-400 dark:text-slate-500">/</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}
